/**
 * System Integration Templates Library
 *
 * This file contains pre-configured templates for popular system integrations.
 * Each template includes default settings, authentication types, endpoint configurations,
 * field definitions, and service endpoints to help users quickly set up common integrations.
 */

// Import constants and templates
import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './service-templates/constants.js'
import { SYSTEM_TEMPLATES } from './service-templates/index.js'

// Re-export everything
export { AUTH_TYPES, TEMPLATE_CATEGORIES, SYSTEM_TEMPLATES }

/**
 * Get template by ID
 * @param {string} templateId - The template identifier
 * @returns {Object|null} The template object or null if not found
 */
export function getTemplateById (templateId) {
	if (!templateId || !SYSTEM_TEMPLATES) {
		return null
	}
	return SYSTEM_TEMPLATES.find(template => template.id === templateId) || null
}

/**
 * Get templates by category
 * @param {string} category - The template category
 * @returns {Array} Array of templates in the specified category
 */
export function getTemplatesByCategory (category) {
	// Input validation for category parameter
	if (!category || typeof category !== 'string' || category.trim() === '') {
		console.warn('getTemplatesByCategory: category must be a non-empty string, received:', category)
		return []
	}

	return SYSTEM_TEMPLATES.filter(template => template.category === category)
}

/**
 * Get all template categories
 * @returns {Array} Array of unique categories
 */
export function getAllCategories () {
	if (!SYSTEM_TEMPLATES || !Array.isArray(SYSTEM_TEMPLATES)) {
		return []
	}
	return [...new Set(SYSTEM_TEMPLATES.map(template => template.category))]
}

/**
 * Get templates sorted by popularity
 * @returns {Array} Array of templates sorted by popularity
 */
export function getTemplatesByPopularity () {
	const popularityOrder = {
		'Most Popular': 1,
		Popular: 2,
		Enterprise: 3,
		Growing: 4,
		Common: 5
	}

	return [...SYSTEM_TEMPLATES].sort((a, b) => {
		return (popularityOrder[a.popularity] || 999) - (popularityOrder[b.popularity] || 999)
	})
}

/**
 * Generate system integration defaults from template
 * @param {Object} template - The template object
 * @param {Object} baseDefaults - Base default values to merge with
 * @returns {Object} Complete system integration defaults
 */
export function generateSystemIntegrationDefaults (template, baseDefaults = {}) {
	if (!template || !template.defaults) {
		return baseDefaults
	}

	return {
		...baseDefaults,
		integrationSystemName: template.name,
		notes: template.description,
		templateGUID: template.guid ? template.guid : null,
		...template.defaults,
		frequencyInSeconds: 10
	}
}

export default {
	AUTH_TYPES,
	TEMPLATE_CATEGORIES,
	SYSTEM_TEMPLATES,
	getTemplateById,
	getTemplatesByCategory,
	getAllCategories,
	getTemplatesByPopularity,
	generateSystemIntegrationDefaults
}
